import { ProjectMethodologyEnum, WorkOrderChangeStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useDeleteBimOrderChange } from 'pages/Project/BimProject/hooks/useDeleteBimOrderChange';
import { methodologyMap } from 'types/methodology';
import type { Position } from 'uikit';

import { useMemo, useCallback, useState } from 'react';

export enum TooltipNameWorkOrderChange {
    DeleteWorkOrderChange = 'DeleteWorkOrderChange',
}

interface IUseAddItemsKebabTreeWorkOrderChange {
    statusWorkOrderChange: WorkOrderChangeStatusEnum;
    isDisabledByStatusProjectAndContract: boolean;
    methodology: string | undefined;
    toggleDeleteHandler: () => void;
    toggleExportWorkOrderChange: () => void;
    toggleEditHandler: (val: boolean) => void;
    workOrderChangeId: number;
    workOrderChangeUerRules: IUseAccess['workOrderChangeUer'];
    onOpenExportWorkOrderChangeAnalyticsClick: () => void;
    workOrderId: number;
}

export const useAddItemsKebabTreeWorkOrderChange = (props: IUseAddItemsKebabTreeWorkOrderChange) => {
    const {
        statusWorkOrderChange,
        isDisabledByStatusProjectAndContract,
        methodology,
        toggleDeleteHandler,
        toggleEditHandler,
        workOrderChangeId,
        workOrderChangeUerRules,
        onOpenExportWorkOrderChangeAnalyticsClick,
        toggleExportWorkOrderChange,
        workOrderId,
    } = props;

    const {
        orderChangeDeleteBtnTitle: bimOrderChangeDeleteBtnTitle,
        deleteBimOrderChangeHandler,
        DeleteOrderChangeErrorHandler: BimDeleteOrderChangeErrorHandler,
        DeleteOrderChangeConfirmDialog: BimDeleteOrderChangeConfirmDialog,
    } = useDeleteBimOrderChange({
        workOrderChangeId,
        workOrderId,
    });

    const isWorkOrderChangeStatusOnFormation = statusWorkOrderChange !== WorkOrderChangeStatusEnum.OnFormation;
    const isApprovedWorkOrderChangeStatus = statusWorkOrderChange === WorkOrderChangeStatusEnum.Approved;

    const kebabItems = useMemo(() => {
        if (methodology === methodologyMap[ProjectMethodologyEnum.Bim]) {
            return [
                {
                    label: 'Выгрузить аналитику изменения НЗ',
                    group: 1,
                    action: onOpenExportWorkOrderChangeAnalyticsClick,
                },
                {
                    label: 'Выгрузить Изм. к НЗ',
                    group: 1,
                    action: toggleExportWorkOrderChange,
                },
                {
                    label: bimOrderChangeDeleteBtnTitle,
                    action: deleteBimOrderChangeHandler,
                    disabled: isDisabledByStatusProjectAndContract || isWorkOrderChangeStatusOnFormation,
                    haveTooltip: isWorkOrderChangeStatusOnFormation,
                    tooltipName: TooltipNameWorkOrderChange.DeleteWorkOrderChange,
                    group: 2,
                },
            ];
        }

        return [
            {
                label: 'Выгрузить аналитику изменения НЗ',
                action: onOpenExportWorkOrderChangeAnalyticsClick,
            },
            {
                label: 'Выгрузить Изм. к НЗ',
                action: toggleExportWorkOrderChange,
            },
            {
                label: isApprovedWorkOrderChangeStatus ? 'Просмотреть данные' : 'Редактировать данные',
                action: () => {
                    toggleEditHandler(true);
                },
                disabled: !isApprovedWorkOrderChangeStatus && !workOrderChangeUerRules.canEdit,
            },
            {
                label: 'Удалить Изм. к НЗ',
                action: () => {
                    toggleDeleteHandler();
                },
                disabled:
                    isDisabledByStatusProjectAndContract ||
                    isWorkOrderChangeStatusOnFormation ||
                    !workOrderChangeUerRules.canDelete,
                haveTooltip: isWorkOrderChangeStatusOnFormation,
                tooltipName: TooltipNameWorkOrderChange.DeleteWorkOrderChange,
            },
        ];
    }, [
        bimOrderChangeDeleteBtnTitle,
        deleteBimOrderChangeHandler,
        isApprovedWorkOrderChangeStatus,
        isDisabledByStatusProjectAndContract,
        isWorkOrderChangeStatusOnFormation,
        methodology,
        onOpenExportWorkOrderChangeAnalyticsClick,
        toggleDeleteHandler,
        toggleEditHandler,
        toggleExportWorkOrderChange,
        workOrderChangeUerRules.canDelete,
        workOrderChangeUerRules.canEdit,
    ]);

    const getMenuItemAttributes = useCallback((item: { haveTooltip: boolean; tooltipName: string }) => {
        if (item.haveTooltip) {
            if (item.tooltipName === TooltipNameWorkOrderChange.DeleteWorkOrderChange) {
                return {
                    readOnly: true,
                    name: TooltipNameWorkOrderChange.DeleteWorkOrderChange,
                };
            }
        }

        return undefined;
    }, []) as any;

    const [tooltipPosition, setTooltipPosition] = useState<Position>(undefined);
    const [activeTooltip, setActiveTooltip] = useState<{ [key: string]: boolean }>({});

    const onMenuMouseMove = useCallback((event) => {
        const attr = event.target.getAttributeNames();
        const hasTooltip = attr.includes('readonly');
        const target = event.target.getAttribute('name');

        if (hasTooltip) {
            if (target === TooltipNameWorkOrderChange.DeleteWorkOrderChange) {
                setActiveTooltip({ [TooltipNameWorkOrderChange.DeleteWorkOrderChange]: true });
            }

            setTooltipPosition({ x: event.clientX, y: event.clientY });
        } else {
            setActiveTooltip({});
        }
    }, []);

    const onMenuMouseLeave = useCallback(() => {
        setActiveTooltip({});
    }, []);

    return {
        kebabItems,
        getMenuItemAttributes,
        tooltipPosition,
        activeTooltip,
        onMenuMouseMove,
        onMenuMouseLeave,
        BimDeleteOrderChangeErrorHandler,
        BimDeleteOrderChangeConfirmDialog,
    };
};
